import { useReducer } from 'react';

/**
 * Types d'actions pour le reducer
 */
const ACTIONS = {
  // Navigation
  SET_ACTIVE_TAB: 'SET_ACTIVE_TAB',

  // Données
  SET_FACTURES_EXTERNES: 'SET_FACTURES_EXTERNES',
  SET_FACTURES_INTERNES: 'SET_FACTURES_INTERNES',
  SET_PRESTATAIRES: 'SET_PRESTATAIRES',
  SET_CONSULTANTS: 'SET_CONSULTANTS',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',

  // Filtres
  SET_FILTER_ANNEE: 'SET_FILTER_ANNEE',
  SET_FILTER_MOIS: 'SET_FILTER_MOIS',
  SET_FILTER_TRIMESTRE: 'SET_FILTER_TRIMESTRE',
  SET_FILTER_STATUT: 'SET_FILTER_STATUT',
  SET_FILTER_PRESTATAIRE: 'SET_FILTER_PRESTATAIRE',
  SET_FILTER_CONSULTANT: 'SET_FILTER_CONSULTANT',
  SET_HIDE_PAID: 'SET_HIDE_PAID',

  // Tri
  SET_SORT_COLUMN: 'SET_SORT_COLUMN',
  SET_SORT_DIRECTION: 'SET_SORT_DIRECTION',

  // Modales
  OPEN_DETAIL_MODAL: 'OPEN_DETAIL_MODAL',
  CLOSE_DETAIL_MODAL: 'CLOSE_DETAIL_MODAL',
  OPEN_EDIT_FACTURE_INTERNE_MODAL: 'OPEN_EDIT_FACTURE_INTERNE_MODAL',
  CLOSE_EDIT_FACTURE_INTERNE_MODAL: 'CLOSE_EDIT_FACTURE_INTERNE_MODAL',
  UPDATE_EDITING_FACTURE_INTERNE: 'UPDATE_EDITING_FACTURE_INTERNE',
  OPEN_DATE_EMISSION_MODAL: 'OPEN_DATE_EMISSION_MODAL',
  CLOSE_DATE_EMISSION_MODAL: 'CLOSE_DATE_EMISSION_MODAL',
  OPEN_EMAIL_PREVIEW_MODAL: 'OPEN_EMAIL_PREVIEW_MODAL',
  CLOSE_EMAIL_PREVIEW_MODAL: 'CLOSE_EMAIL_PREVIEW_MODAL',
  OPEN_PAYMENT_MODAL: 'OPEN_PAYMENT_MODAL',
  CLOSE_PAYMENT_MODAL: 'CLOSE_PAYMENT_MODAL',
  OPEN_PAYMENT_HISTORY_MODAL: 'OPEN_PAYMENT_HISTORY_MODAL',
  CLOSE_PAYMENT_HISTORY_MODAL: 'CLOSE_PAYMENT_HISTORY_MODAL',
  OPEN_AJOUTER_MODAL: 'OPEN_AJOUTER_MODAL',
  CLOSE_AJOUTER_MODAL: 'CLOSE_AJOUTER_MODAL',
  OPEN_PDF_VIEWER: 'OPEN_PDF_VIEWER',
  CLOSE_PDF_VIEWER: 'CLOSE_PDF_VIEWER',
  OPEN_DATE_DOWNLOAD_INTERNE_MODAL: 'OPEN_DATE_DOWNLOAD_INTERNE_MODAL',
  CLOSE_DATE_DOWNLOAD_INTERNE_MODAL: 'CLOSE_DATE_DOWNLOAD_INTERNE_MODAL',

  // Statut update
  SET_UPDATING_STATUT: 'SET_UPDATING_STATUT',
  SET_EDITING_STATUT: 'SET_EDITING_STATUT'
};

/**
 * État initial
 */
const initialState = (currentYear, initialTab = 'externe') => ({
  // Navigation
  activeTab: initialTab,

  // Données
  facturesExternes: [],
  facturesInternes: [],
  prestataires: [],
  consultants: [],
  loading: true,
  error: null,

  // Filtres
  filterAnnee: currentYear,
  filterMois: [],
  filterTrimestre: '',
  filterStatut: [],
  filterPrestataire: [],
  filterConsultant: '',
  hidePaid: false,

  // Tri
  sortColumn: 'numero',
  sortDirection: 'desc',

  // Modale détail
  showDetailModal: false,
  selectedFacture: null,
  isModalReadOnly: false,

  // Statut update
  updatingStatut: false,
  editingStatut: null,

  // Modale édition facture interne
  showEditFactureInterneModal: false,
  editingFactureInterne: {
    id: null,
    consultant: '',
    consultantNom: '',
    annee: null,
    trimestre: '',
    numeroFacture: '',
    montantHT: 0,
    tauxTVA: 20,
    intitule: '',
    lines: [],
    destinataireType: 'ephyco',
    prestataireId: null,
    statut: 'Brouillon'
  },

  // Modale date émission
  showDateEmissionModal: false,
  dateEmissionFacture: null,
  dateEmissionPrestataire: null,

  // Modale aperçu email
  showEmailPreviewModal: false,
  emailFacture: null,
  emailPrestataire: null,

  // Modale paiement
  showPaymentModal: false,
  selectedPaymentFacture: null,

  // Modale historique paiements (factures internes)
  showPaymentHistoryModal: false,
  selectedPaymentFactureInterne: null,

  // Modale ajout
  showAjouterModal: false,
  typeFacture: 'externe',

  // PDF viewer
  showPdfViewer: false,
  pdfUrl: null,

  // Modale date download interne
  showDateDownloadInterneModal: false,
  selectedDownloadFactureInterne: null
});

/**
 * Reducer pour gérer l'état de la facturation
 */
function facturationReducer(state, action) {
  switch (action.type) {
    // Navigation
    case ACTIONS.SET_ACTIVE_TAB:
      return { ...state, activeTab: action.payload };

    // Données
    case ACTIONS.SET_FACTURES_EXTERNES:
      return { ...state, facturesExternes: action.payload };
    case ACTIONS.SET_FACTURES_INTERNES:
      return { ...state, facturesInternes: action.payload };
    case ACTIONS.SET_PRESTATAIRES:
      return { ...state, prestataires: action.payload };
    case ACTIONS.SET_CONSULTANTS:
      return { ...state, consultants: action.payload };
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload };

    // Filtres
    case ACTIONS.SET_FILTER_ANNEE:
      return { ...state, filterAnnee: action.payload };
    case ACTIONS.SET_FILTER_MOIS:
      return { ...state, filterMois: action.payload };
    case ACTIONS.SET_FILTER_TRIMESTRE:
      return { ...state, filterTrimestre: action.payload };
    case ACTIONS.SET_FILTER_STATUT:
      return { ...state, filterStatut: action.payload };
    case ACTIONS.SET_FILTER_PRESTATAIRE:
      return { ...state, filterPrestataire: action.payload };
    case ACTIONS.SET_FILTER_CONSULTANT:
      return { ...state, filterConsultant: action.payload };
    case ACTIONS.SET_HIDE_PAID:
      return { ...state, hidePaid: action.payload };

    // Tri
    case ACTIONS.SET_SORT_COLUMN:
      return { ...state, sortColumn: action.payload };
    case ACTIONS.SET_SORT_DIRECTION:
      return { ...state, sortDirection: action.payload };

    // Modales - Détail
    case ACTIONS.OPEN_DETAIL_MODAL:
      return {
        ...state,
        showDetailModal: true,
        selectedFacture: action.payload.facture,
        isModalReadOnly: action.payload.readOnly
      };
    case ACTIONS.CLOSE_DETAIL_MODAL:
      return {
        ...state,
        showDetailModal: false,
        selectedFacture: null,
        isModalReadOnly: false
      };

    // Modales - Édition facture interne
    case ACTIONS.OPEN_EDIT_FACTURE_INTERNE_MODAL:
      return {
        ...state,
        showEditFactureInterneModal: true,
        editingFactureInterne: action.payload
      };
    case ACTIONS.CLOSE_EDIT_FACTURE_INTERNE_MODAL:
      return {
        ...state,
        showEditFactureInterneModal: false,
        editingFactureInterne: {
          id: null,
          consultant: '',
          consultantNom: '',
          annee: null,
          trimestre: '',
          numeroFacture: '',
          montantHT: 0,
          tauxTVA: 20,
          intitule: '',
          lines: [],
          destinataireType: 'ephyco',
          prestataireId: null,
          statut: 'Brouillon'
        }
      };
    case ACTIONS.UPDATE_EDITING_FACTURE_INTERNE:
      return {
        ...state,
        editingFactureInterne: {
          ...state.editingFactureInterne,
          [action.payload.field]: action.payload.value
        }
      };

    // Modales - Date émission
    case ACTIONS.OPEN_DATE_EMISSION_MODAL:
      return {
        ...state,
        showDateEmissionModal: true,
        dateEmissionFacture: action.payload.facture,
        dateEmissionPrestataire: action.payload.prestataire
      };
    case ACTIONS.CLOSE_DATE_EMISSION_MODAL:
      return {
        ...state,
        showDateEmissionModal: false,
        dateEmissionFacture: null,
        dateEmissionPrestataire: null
      };

    // Modales - Aperçu email
    case ACTIONS.OPEN_EMAIL_PREVIEW_MODAL:
      return {
        ...state,
        showEmailPreviewModal: true,
        emailFacture: action.payload.facture,
        emailPrestataire: action.payload.prestataire
      };
    case ACTIONS.CLOSE_EMAIL_PREVIEW_MODAL:
      return {
        ...state,
        showEmailPreviewModal: false,
        emailFacture: null,
        emailPrestataire: null
      };
      case ACTIONS.RESET_EMAIL_FLOW:
        return {
          ...state,
          showDateEmissionModal: false,
          showEmailPreviewModal: false,
          dateEmissionFacture: null,
          dateEmissionPrestataire: null,
          emailFacture: null,
          emailPrestataire: null
        };

    // Modales - Paiement
    case ACTIONS.OPEN_PAYMENT_MODAL:
      return {
        ...state,
        showPaymentModal: true,
        selectedPaymentFacture: action.payload
      };
    case ACTIONS.CLOSE_PAYMENT_MODAL:
      return {
        ...state,
        showPaymentModal: false,
        selectedPaymentFacture: null
      };

    // Modales - Historique paiements
    case ACTIONS.OPEN_PAYMENT_HISTORY_MODAL:
      return {
        ...state,
        showPaymentHistoryModal: true,
        selectedPaymentFactureInterne: action.payload
      };
    case ACTIONS.CLOSE_PAYMENT_HISTORY_MODAL:
      return {
        ...state,
        showPaymentHistoryModal: false,
        selectedPaymentFactureInterne: null
      };

    // Modales - Ajouter
    case ACTIONS.OPEN_AJOUTER_MODAL:
      return {
        ...state,
        showAjouterModal: true,
        typeFacture: action.payload || 'externe'
      };
    case ACTIONS.CLOSE_AJOUTER_MODAL:
      return {
        ...state,
        showAjouterModal: false
      };

    // PDF Viewer
    case ACTIONS.OPEN_PDF_VIEWER:
      return {
        ...state,
        showPdfViewer: true,
        pdfUrl: action.payload
      };
    case ACTIONS.CLOSE_PDF_VIEWER:
      return {
        ...state,
        showPdfViewer: false,
        pdfUrl: null
      };

    // Modale date download interne
    case ACTIONS.OPEN_DATE_DOWNLOAD_INTERNE_MODAL:
      return {
        ...state,
        showDateDownloadInterneModal: true,
        selectedDownloadFactureInterne: action.payload
      };
    case ACTIONS.CLOSE_DATE_DOWNLOAD_INTERNE_MODAL:
      return {
        ...state,
        showDateDownloadInterneModal: false,
        selectedDownloadFactureInterne: null
      };

    // Statut update
    case ACTIONS.SET_UPDATING_STATUT:
      return { ...state, updatingStatut: action.payload };
    case ACTIONS.SET_EDITING_STATUT:
      return { ...state, editingStatut: action.payload };

    default:
      return state;
  }
}

/**
 * Hook personnalisé pour gérer l'état de la facturation
 *
 * @param {number} currentYear - Année courante pour filtrer par défaut
 * @param {string} initialTab - Onglet actif par défaut ('externe' ou 'interne')
 * @returns {Object} État et dispatch
 */
export function useFacturationState(currentYear, initialTab = 'externe') {
  const [state, dispatch] = useReducer(facturationReducer, { currentYear, initialTab }, (params) =>
    initialState(params.currentYear, params.initialTab)
  );

  return { state, dispatch, ACTIONS };
}
